/* tslint:disable:max-file-line-count */
import { cloneDeep } from 'lodash';
import { IBookingsStatistics, IEventV2, IEventV2Api, IEventV3, IEventV3Api } from '../data/entities/event';
import { IBookingReceipt, IBookingReceiptApi, IBookingV2, IBookingV2Api } from '../data/entities/event/booking';
import { IOwnerExtras } from '../data/entities/event/v3/index';
import { IAppliedPromotion } from '../data/entities/promotions';
import { fromFloyd, toFloyd } from '../entities/pricing/helpers';
import { stripTimezone } from '../view/helpers/date_helpers';
import { normaliseQueryParameters, queryParametersToQueryString } from './helpers';
import { IConfiguredHttp } from './http';
import { transformInboundBookingEventV3 } from './transformers';

type EventStatus = 'booked' | 'cancelled' | 'failed' | 'transferred';
type TimePeriod = 'past' | 'present' | 'future';
type SortDirection = 'ascending' | 'descending';

export interface IBookingsByOwnerIdParams {
	start_on?: string;
	time_period?: TimePeriod[];
	page_number?: number;
	page_size?: number;
	event_status?: EventStatus[];
	sort_direction?: SortDirection;
	summary?: boolean;
}

export interface IBookingsByPropertyIdParams {
	start_date_range?: string;
	end_date_range?: string;
	time_period?: TimePeriod[];
	page_number?: number;
	page_size?: number;
	event_status?: EventStatus[];
	sort_direction?: SortDirection;
}

export interface IBookingsStatisticsByOwnerIdParams {
	previous_num_days?: number;
	next_num_days?: number;
}

export interface IAvailabilityV3BookingsResponse {
	data: IEventV3[];
	_stats: {
		page_number: number;
		page_size: number;
		total: number;
	};
}
export interface IBookingsApi {
	getBookingSummariesByPropertyId: (id: string, params?: IBookingsByPropertyIdParams) => Promise<IAvailabilityV3BookingsResponse>;
	getBookingById: (propertyId: string, id: string) => Promise<IEventV2>;
	getFirstBookingIdByOwnerId: (ownerId: string) => Promise<string>;
	getBookingsByOwnerId: (ownerId: string, params?: IBookingsByOwnerIdParams) => Promise<IAvailabilityV3BookingsResponse>;
	getBookingsStatsByOwnerId: (ownerId: string, params?: IBookingsStatisticsByOwnerIdParams) => Promise<IBookingsStatistics>;
	updateBooking: (propertyId: string, id: string, event: IEventV2) => Promise<IEventV2>;
	cancelBooking: (propertyId: string, id: string) => Promise<IEventV2>;
	updateBookingV3: (propertyId: string, id: string, event: IEventV2) => Promise<IEventV2>;
	transferBooking: (propertyId: string, id: string) => Promise<IEventV2>;
}

const BookingsApiFactory = (http: IConfiguredHttp, prefix = ''): IBookingsApi => ({
	getBookingSummariesByPropertyId(id, params) {
		return http.get(`${prefix}/bookings/property/${id}?${queryParametersToQueryString(normaliseQueryParameters(params))}`)
			.then((resp: IAvailabilityV3BookingsResponse) => ({
				...resp,
				data: resp.data.map((d) => transformInboundBookingEventV3(d))
			}));
	},
	getBookingById(propertyId, id) {
		clearLocalAuditStorage();
		return http.get(`${prefix}/bookings/property/${propertyId}/id/${id}`).then((data: IEventV3Api) => (transformInboundV3BookingEventToV2(data)));
	},
	getFirstBookingIdByOwnerId(ownerId) {
		return http.get(`${prefix}/bookings/customer/${ownerId}/first_booking`).then((data: IEventV3Api) => (data.id));
	},
	getBookingsByOwnerId(ownerId, params) {
		return http.get(`${prefix}/bookings/customer/${ownerId}?${queryParametersToQueryString(normaliseQueryParameters(params))}`)
			.then((resp: IAvailabilityV3BookingsResponse) => ({
				...resp,
				data: resp.data.map((d) => transformInboundBookingEventV3(d))
			}));
	},
	getBookingsStatsByOwnerId(ownerId, params) {
		return http.get(`${prefix}/bookings/customer/${ownerId}/statistics?${queryParametersToQueryString(normaliseQueryParameters(params))}`)
			.then((data: IBookingsStatistics) => data);
	},
	updateBooking(propertyId, id, event) {
		return http.put(`/availability/v2/property/${propertyId}/events/${id}`,
			transformOutboundBookingEvent(event)).then((data: IEventV2Api) => transformInboundBookingEvent(data));
	},
	updateBookingV3(propertyId, id, event) {
		// Careful using the v3 endpoint to update bookings as it behaves in a different way to v2
		// Notes: notes will be updated to match what is sent on the booking object
		// FX updates: fx adjustments will update the fx rate to the rate at current date
		// External ids: source_id is id so they need to by swapped around.
		return http.put(`${prefix}/bookings/property/${propertyId}/id/${id}`,
			transformOutboundV2BookingEventToV3(event)).then((data: IEventV3Api) => transformInboundV3BookingEventToV2(data));
	},
	cancelBooking(propertyId, id) {
		return http.delete(`${prefix}/bookings/property/${propertyId}/id/${id}`).then((data: IEventV3Api) => transformInboundV3BookingEventToV2(data));
	},
	transferBooking(propertyId, id) {
		return http.post(`${prefix}/bookings/property/${propertyId}/id/${id}/transfer`)
			.then((data: IEventV3Api) => transformInboundV3BookingEventToV2(data));
	}
});

// Clear session storage on new booking fetch
const clearLocalAuditStorage = () => {
	try {
		const sessionAuditsKeys = [
			'deleted_owner_payments',
			'added_owner_payments',
			'deleted_guest_payments',
			'added_guest_payments',
			'old_owner_rent_value',
			'new_owner_rent_value',
			'old_cash_receipt_value',
			'new_cash_receipt_value',
			'old_start_on',
			'new_start_on',
			'old_end_on',
			'new_end_on'
		];
		sessionAuditsKeys.forEach((key) => sessionStorage.removeItem(key));
	} catch (err) {
		console.warn('Unable to remove session audit keys');
	}
};

export const transformOutboundBookingEvent = (bookingEvent: IEventV2): IEventV2Api => {
	const createdAt = bookingEvent.created_at.toISOString();
	const updatedAt = bookingEvent.updated_at.toISOString();
	return {
		...bookingEvent,
		booking: bookingEvent.booking ? transformOutboundBooking(bookingEvent.booking) : bookingEvent.booking as any,
		created_at: createdAt,
		updated_at: updatedAt
	};
};

const transformInboundBookingEvent = (bookingEvent: IEventV2Api): IEventV2 => {
	const createdAt = new Date(bookingEvent.created_at);
	const updatedAt = new Date(bookingEvent.updated_at);
	return {
		...bookingEvent,
		booking: bookingEvent.booking ? transformInboundBooking(bookingEvent.booking) : bookingEvent.booking as any,
		created_at: createdAt,
		updated_at: updatedAt
	};
};

export const transformInboundV3BookingEventToV2 = (bookingEvent: IEventV3Api): IEventV2 => {
	const { booking } = bookingEvent;
	const { booking_value } = booking!;
	const createdAt = new Date(bookingEvent.created_at);
	const updatedAt = new Date(booking_value.updated_at);

	const event = {
		...bookingEvent,
		created_at: createdAt,
		updated_at: updatedAt
	};
	const bookingValue = {
		...bookingEvent.booking,
		cancellation_policy: bookingEvent.booking?.cancellation_policy,
		actual_cancellation_policy: bookingEvent.booking?.actual_cancellation_policy,
		status: bookingEvent.booking?.status,
		guest: {
			contact: {
				...booking?.guest
			},
			party: {
				adults: booking?.guest?.adults,
				children: booking?.guest?.children,
				infants: booking?.guest?.infants
			}
		},
		guest_receipts: {
			receipts: (booking?.guest_receipts || []).map((receipts) => ({ ...receipts, date: receipts.on_date })),
			status: booking?.guest_receipt_status
		},
		owner_payments: {
			payments: (booking?.owner_payments || []).map((payments) => ({ ...payments, date: payments.on_date })),
			status: booking?.owner_payment_status
		},
		security_deposit: booking?.deposit_status ? {
			status: booking?.deposit_status,
			receipts: (booking?.deposits || []).map((deposits) => ({ ...deposits, date: deposits.on_date })),
		} : null,
		value: {
			calculator_id: booking_value.calculator_id,
			currency: booking_value.currency,
			total: booking_value.tbv,
			booking_fee_spec: booking_value.booking_fee_spec,
			breakdown: {
				channel: {
					cash_receipt_currency: booking_value.channel_cash_receipt_currency,
					cash_receipt_value: booking_value.channel_cash_receipt,
					commission_net_value: booking_value.channel_commission_net_value,
					commission_rate: booking_value.channel_commission_rate,
					commission_value: booking_value.channel_commission_value,
					commission_vat_value: booking_value.channel_commission_vat_value,
					converted_cash_receipt_currency: booking_value.converted_channel_cash_receipt_currency,
					converted_cash_receipt_fx_rate: booking_value.converted_channel_cash_receipt_fx_rate,
					converted_cash_receipt_value: booking_value.converted_channel_cash_receipt,
					deducts_commission: booking_value.channel_deducts_commission,
					effective_total_booking_value: booking_value.effective_total_booking_value,
					guest_payment_value: booking_value.guest_payment_value,
					vat_rate: booking_value.channel_vat_rate,
					tbv: booking_value.tbv
				},
				owner: {
					discounted_owner_rent_value: booking_value.discounted_owner_rent_value,
					effective_rent_value: booking_value.effective_owner_rent_value,
					extras: {
						items: booking_value.owner_extras,
						total: (booking_value.owner_extras || []).reduce((total, extra) => total += Number(extra.amount), 0)
					},
					payment_value: booking_value.owner_payment_value,
					rent_value: booking_value.owner_rent_value,
					tourist_tax: booking_value.tourist_tax,
					converted_tourist_tax: booking_value.converted_tourist_tax,
					tourist_tax_type: booking_value.tourist_tax_type
				},
				travelnest: {
					booking_balance: booking_value.tn_booking_balance,
					booking_discrepancy: booking_value.tn_booking_discrepancy,
					booking_discrepancy_net_value: booking_value.tn_booking_discrepancy_net_value,
					booking_discrepancy_vat_value: booking_value.tn_booking_discrepancy_vat_value,
					commission_net_value: booking_value.tn_commission_net_value,
					commission_rate: booking_value.tn_commission_rate,
					commission_value: booking_value.tn_commission_value,
					commission_vat_value: booking_value.tn_commission_vat_value,
					expected_booking_fee_net_value: booking_value.tn_expected_booking_fee_net_value,
					expected_booking_fee_rate: booking_value.tn_expected_booking_fee_rate,
					expected_booking_fee_value: booking_value.tn_expected_booking_fee_value,
					expected_booking_fee_vat_value: booking_value.tn_expected_booking_fee_vat_value,
					income_value: booking_value.tn_income_value,
					margin_rate: booking_value.tn_margin_rate,
					margin_value: booking_value.tn_margin_value,
					notional_profit_rate: booking_value.tn_notional_profit_rate,
					presented_channel_net_value: booking_value.presented_channel_net_value,
					presented_channel_rate: booking_value.presented_channel_rate,
					presented_channel_value: booking_value.presented_channel_value,
					presented_channel_vat_value: booking_value.presented_channel_vat_value,
					presented_travelnest_net_value: booking_value.presented_travelnest_net_value,
					presented_travelnest_rate: booking_value.presented_travelnest_rate,
					presented_travelnest_value: booking_value.presented_travelnest_value,
					presented_travelnest_vat_value: booking_value.presented_travelnest_vat_value,
					total_adjustment_commission_net_value: booking_value.total_adjustment_commission_net_value,
					total_adjustment_commission_rate: booking_value.total_adjustment_commission_rate,
					total_adjustment_commission_value: booking_value.total_adjustment_commission_value,
					total_adjustment_commission_vat_value: booking_value.total_adjustment_commission_vat_value,
					vat_rate: booking_value.tn_vat_rate,
					id: booking_value.id
				},
			},
		},
		external_id: booking?.external_ids?.map((ids) => ({ ...ids, id: ids.source_id || ids.id, source_id: ids.id })),
	};

	event.booking = transformInboundBooking(bookingValue as any) as any;
	return event as any;
};

const valueOrZero = (value: any) => value ? value : 0;

export const transformOutboundV2BookingEventToV3 = (bookingEvent: IEventV2): IEventV3Api => {
	const createdAt = typeof bookingEvent.created_at === 'string' ? bookingEvent.created_at : bookingEvent.created_at.toISOString();
	const updatedAt = typeof bookingEvent.updated_at === 'string' ? bookingEvent.updated_at : bookingEvent.updated_at.toISOString();
	const booking = transformOutboundBooking(bookingEvent.booking!);
	const { breakdown } = booking.value;

	return {
		created_at: createdAt,
		updated_at: updatedAt,
		id: bookingEvent.id,
		managed_by: bookingEvent.managed_by,
		notes: bookingEvent.notes,
		property_id: bookingEvent.property_id,
		source: bookingEvent.source,
		start_on: bookingEvent.start_on,
		end_on: bookingEvent.end_on,
		status: bookingEvent.status,
		booking: {
			booking_value: {
				booking_fee_spec: booking.value.booking_fee_spec as any,
				calculator_id: booking.value.calculator_id,
				channel_cash_receipt: breakdown.channel.cash_receipt_value,
				channel_cash_receipt_currency: breakdown.channel.cash_receipt_currency,
				channel_commission_net_value: valueOrZero(breakdown.channel.commission_net_value),
				channel_commission_rate: valueOrZero(breakdown.channel.commission_rate),
				channel_commission_value: valueOrZero(breakdown.channel.commission_value),
				channel_commission_vat_value: valueOrZero(breakdown.channel.commission_vat_value),
				channel_deducts_commission: valueOrZero(breakdown.channel.deducts_commission),
				channel_vat_rate: valueOrZero(breakdown.channel.vat_rate),
				converted_channel_cash_receipt: breakdown.channel.converted_cash_receipt_value,
				converted_channel_cash_receipt_currency: breakdown.channel.converted_cash_receipt_currency,
				converted_channel_cash_receipt_fx_rate: breakdown.channel.converted_cash_receipt_fx_rate,
				created_at: createdAt,
				currency: booking.value.currency,
				discounted_owner_rent_value: valueOrZero(breakdown.owner?.discounted_owner_rent_value),
				effective_owner_rent_value: valueOrZero(breakdown.owner?.effective_rent_value),
				effective_total_booking_value: valueOrZero(breakdown.channel?.effective_total_booking_value),
				guest_payment_value: valueOrZero(breakdown.channel.guest_payment_value),
				id: breakdown.travelnest?.id as any,
				owner_extras: (breakdown.owner?.extras.items || []).map((owner) => ({ ...owner, updated_at: updatedAt })) as IOwnerExtras[],
				owner_payment_value: valueOrZero(breakdown.owner?.payment_value),
				owner_rent_value: valueOrZero(breakdown.owner?.rent_value),
				presented_channel_net_value: valueOrZero(breakdown.travelnest?.presented_channel_net_value),
				presented_channel_rate: valueOrZero(breakdown.travelnest?.presented_channel_rate),
				presented_channel_value: valueOrZero(breakdown.travelnest?.presented_channel_value),
				presented_channel_vat_value: valueOrZero(breakdown.travelnest?.presented_channel_vat_value),
				presented_travelnest_net_value: valueOrZero(breakdown.travelnest?.presented_travelnest_net_value),
				presented_travelnest_rate: valueOrZero(breakdown.travelnest?.presented_travelnest_rate),
				presented_travelnest_value: valueOrZero(breakdown.travelnest?.presented_travelnest_value),
				presented_travelnest_vat_value: valueOrZero(breakdown.travelnest?.presented_channel_vat_value),
				tbv: valueOrZero(breakdown.channel.tbv),
				tn_booking_balance: valueOrZero(breakdown.travelnest?.booking_balance),
				tn_booking_discrepancy: valueOrZero(breakdown.travelnest?.booking_discrepancy),
				tn_booking_discrepancy_net_value: valueOrZero(breakdown.travelnest?.booking_discrepancy_net_value),
				tn_booking_discrepancy_vat_value: valueOrZero(breakdown.travelnest?.booking_discrepancy_vat_value),
				tn_commission_net_value: valueOrZero(breakdown.travelnest?.commission_net_value),
				tn_commission_rate: valueOrZero(breakdown.travelnest?.commission_rate),
				tn_commission_value: valueOrZero(breakdown.travelnest?.commission_value),
				tn_commission_vat_value: valueOrZero(breakdown.travelnest?.commission_vat_value),
				tn_expected_booking_fee_net_value: valueOrZero(breakdown.travelnest?.expected_booking_fee_net_value),
				tn_expected_booking_fee_rate: valueOrZero(breakdown.travelnest?.expected_booking_fee_rate),
				tn_expected_booking_fee_value: valueOrZero(breakdown.travelnest?.expected_booking_fee_rate),
				tn_expected_booking_fee_vat_value: valueOrZero(breakdown.travelnest?.expected_booking_fee_vat_value),
				tn_income_value: valueOrZero(breakdown.travelnest?.income_value),
				tn_margin_rate: valueOrZero(breakdown.travelnest?.margin_rate),
				tn_margin_value: valueOrZero(breakdown.travelnest?.margin_value),
				tn_notional_profit_rate: valueOrZero(breakdown.travelnest?.notional_profit_rate),
				tn_vat_rate: valueOrZero(breakdown.travelnest?.vat_rate),
				total_adjustment_commission_net_value: valueOrZero(breakdown.travelnest?.total_adjustment_commission_net_value),
				total_adjustment_commission_rate: valueOrZero(breakdown.travelnest?.total_adjustment_commission_rate),
				total_adjustment_commission_value: valueOrZero(breakdown.travelnest?.total_adjustment_commission_value),
				total_adjustment_commission_vat_value: valueOrZero(breakdown.travelnest?.total_adjustment_commission_vat_value),
				updated_at: updatedAt,
				tourist_tax: valueOrZero(breakdown.owner?.tourist_tax),
				converted_tourist_tax: valueOrZero(breakdown.owner?.converted_tourist_tax),
				tourist_tax_type: breakdown.owner?.tourist_tax_type || 'NO_TAX'
			},
			cancellation_policy: booking.cancellation_policy,
			actual_cancellation_policy: booking.actual_cancellation_policy,
			created_at: createdAt,
			deposit_status: booking.security_deposit?.status || '',
			deposits: (booking.security_deposit?.receipts || []).map((receipts) => ({ ...receipts, updated_at: updatedAt })) as any[],
			external_ids: (booking.external_id || []).map((ids) => ({ ...ids, updated_at: updatedAt, id: ids.source_id || ids.id, source_id: ids.id })),
			guest: { ...booking.guest?.contact, ...booking.guest?.party } as any,
			guest_receipt_status: booking.guest_receipts.status,
			guest_receipts: (booking.guest_receipts.receipts || []).map((receipts) => ({ ...receipts, updated_at: updatedAt })) as any,
			id: booking.value.id as any,
			owner_payment_status: booking.owner_payments.status,
			owner_payments: (booking.owner_payments.payments || []).map((payments) => ({ ...payments, updated_at: updatedAt })) as any,
			updated_at: updatedAt,
			post_payments: booking.post_payments as any,
			status: booking.status
		}
	};
};

const bookingToFloydionPrices = (booking: IBookingV2) => {
	const bookingToFloyd = cloneDeep(booking);
	const breakdown = bookingToFloyd.value.breakdown;
	const owner = breakdown.owner;
	const travelNestBreakdown = bookingToFloyd.value.breakdown.travelnest;

	if (bookingToFloyd.value && bookingToFloyd.value.total) {
		bookingToFloyd.value.total = parseFloat(toFloyd(bookingToFloyd.value.total));
	}
	if (breakdown.channel && breakdown.channel.commission_net_value) {
		breakdown.channel.commission_net_value = parseFloat(toFloyd(breakdown.channel.commission_net_value));
	}
	if (breakdown.channel && breakdown.channel.commission_vat_value) {
		breakdown.channel.commission_vat_value = parseFloat(toFloyd(breakdown.channel.commission_vat_value));
	}
	if (breakdown.channel && breakdown.channel.commission_value) {
		breakdown.channel.commission_value = parseFloat(toFloyd(breakdown.channel.commission_value));
	}

	if (breakdown.channel && breakdown.channel.cash_receipt_value) {
		breakdown.channel.cash_receipt_value = parseFloat(toFloyd(breakdown.channel.cash_receipt_value));
	}

	if (breakdown.channel && breakdown.channel.converted_cash_receipt_value) {
		breakdown.channel.converted_cash_receipt_value = parseFloat(toFloyd(breakdown.channel.converted_cash_receipt_value));
	}

	if (breakdown.channel && breakdown.channel.effective_total_booking_value) {
		breakdown.channel.effective_total_booking_value = parseFloat(toFloyd(breakdown.channel.effective_total_booking_value));
	}

	if (breakdown.channel && breakdown.channel.guest_payment_value) {
		breakdown.channel.guest_payment_value = parseFloat(toFloyd(breakdown.channel.guest_payment_value));
	}

	if (owner) {
		owner.payment_value = parseFloat(toFloyd(owner.payment_value || 0));
		owner.rent_value = parseFloat(toFloyd(owner.rent_value || 0));
		owner.discounted_owner_rent_value = owner.discounted_owner_rent_value ?
			parseFloat(toFloyd(owner.discounted_owner_rent_value || 0)) : owner.rent_value;
		owner.effective_rent_value = parseFloat(toFloyd(owner.effective_rent_value || 0));
		if (owner && owner.extras && owner.extras.items) {
			for (const item of owner.extras.items) {
				item.amount = parseFloat(toFloyd(item.amount || 0));
			}
			owner.extras.total = parseFloat(toFloyd(owner.extras.total || 0));
		}
		if (owner.tourist_tax) {
			owner.tourist_tax = parseFloat(toFloyd(owner.tourist_tax || 0));
		}
		if (owner.converted_tourist_tax) {
			owner.converted_tourist_tax = parseFloat(toFloyd(owner.converted_tourist_tax || 0));
		}
	}

	if (bookingToFloyd.owner_promotional_discounts) {
		bookingToFloyd.owner_promotional_discounts = bookingToFloyd.owner_promotional_discounts.map((p) => ({
			...p,
			discount_amount: parseFloat(toFloyd(p.discount_amount || 0)),
		}));
	}

	if (travelNestBreakdown) {
		travelNestBreakdown.booking_balance = parseFloat(toFloyd(travelNestBreakdown.booking_balance || 0));
		travelNestBreakdown.booking_discrepancy = parseFloat(toFloyd(travelNestBreakdown.booking_discrepancy || 0));
		travelNestBreakdown.booking_discrepancy_net_value = parseFloat(toFloyd(travelNestBreakdown.booking_discrepancy_net_value || 0));
		travelNestBreakdown.booking_discrepancy_vat_value = parseFloat(toFloyd(travelNestBreakdown.booking_discrepancy_vat_value || 0));
		travelNestBreakdown.commission_value = parseFloat(toFloyd(travelNestBreakdown.commission_value || 0));
		travelNestBreakdown.commission_net_value = parseFloat(toFloyd(travelNestBreakdown.commission_net_value || 0));
		travelNestBreakdown.commission_vat_value = parseFloat(toFloyd(travelNestBreakdown.commission_vat_value || 0));
		travelNestBreakdown.expected_booking_fee_net_value = parseFloat(toFloyd(travelNestBreakdown.expected_booking_fee_net_value || 0));
		travelNestBreakdown.expected_booking_fee_vat_value = parseFloat(toFloyd(travelNestBreakdown.expected_booking_fee_vat_value || 0));
		travelNestBreakdown.expected_booking_fee_value = parseFloat(toFloyd(travelNestBreakdown.expected_booking_fee_value || 0));
		travelNestBreakdown.income_value = parseFloat(toFloyd(travelNestBreakdown.income_value || 0));
		travelNestBreakdown.margin_value = parseFloat(toFloyd(travelNestBreakdown.margin_value || 0));
		// 2021 commission columns
		if (travelNestBreakdown.presented_channel_value !== null && travelNestBreakdown.presented_channel_value !== undefined) {
			// perfectly viable for these columns to be null, but if one is null, they all will be.
			// if bookings were created before the commission change, these values will be null - not zero.
			travelNestBreakdown.presented_channel_value = parseFloat(toFloyd(travelNestBreakdown.presented_channel_value || 0));
			travelNestBreakdown.presented_channel_net_value = parseFloat(toFloyd(travelNestBreakdown.presented_channel_net_value || 0));
			travelNestBreakdown.presented_channel_vat_value = parseFloat(toFloyd(travelNestBreakdown.presented_channel_vat_value || 0));
			travelNestBreakdown.presented_travelnest_value = parseFloat(toFloyd(travelNestBreakdown.commission_value || 0));
			travelNestBreakdown.presented_travelnest_net_value = parseFloat(toFloyd(travelNestBreakdown.presented_travelnest_net_value || 0));
			travelNestBreakdown.presented_travelnest_vat_value = parseFloat(toFloyd(travelNestBreakdown.presented_travelnest_vat_value || 0));
			travelNestBreakdown.total_adjustment_commission_value = parseFloat(toFloyd(travelNestBreakdown.total_adjustment_commission_value || 0));
			travelNestBreakdown.total_adjustment_commission_net_value = parseFloat(toFloyd(travelNestBreakdown.total_adjustment_commission_net_value || 0));
			travelNestBreakdown.total_adjustment_commission_vat_value = parseFloat(toFloyd(travelNestBreakdown.total_adjustment_commission_vat_value || 0));
		}
	}
	return bookingToFloyd;
};

const bookingFromFloydionPrices = (booking: IBookingV2Api) => {
	const bookingFromFloyd = cloneDeep(booking);
	const breakdown = bookingFromFloyd.value.breakdown;
	const owner = breakdown.owner;
	const travelNestBreakdown = bookingFromFloyd.value.breakdown.travelnest;
	const promotions = bookingFromFloyd.owner_promotional_discounts;

	if (bookingFromFloyd.value && bookingFromFloyd.value.total) {
		bookingFromFloyd.value.total = fromFloyd(bookingFromFloyd.value.total);
	}
	if (breakdown.channel && breakdown.channel.commission_net_value) {
		breakdown.channel.commission_net_value = fromFloyd(breakdown.channel.commission_net_value);
	}
	if (breakdown.channel && breakdown.channel.commission_vat_value) {
		breakdown.channel.commission_vat_value = fromFloyd(breakdown.channel.commission_vat_value);
	}
	if (breakdown.channel && breakdown.channel.commission_value) {
		breakdown.channel.commission_value = fromFloyd(breakdown.channel.commission_value);
	}

	if (breakdown.channel && breakdown.channel.cash_receipt_value) {
		breakdown.channel.cash_receipt_value = fromFloyd(breakdown.channel.cash_receipt_value);
	}

	if (breakdown.channel && breakdown.channel.converted_cash_receipt_value) {
		breakdown.channel.converted_cash_receipt_value = fromFloyd(breakdown.channel.converted_cash_receipt_value);
	}

	if (breakdown.channel && breakdown.channel.effective_total_booking_value) {
		breakdown.channel.effective_total_booking_value = fromFloyd(breakdown.channel.effective_total_booking_value);
	}

	if (breakdown.channel && breakdown.channel.guest_payment_value) {
		breakdown.channel.guest_payment_value = fromFloyd(breakdown.channel.guest_payment_value);
	}

	if (promotions) {
		promotions.map((p: IAppliedPromotion) => p.discount_amount = fromFloyd(p.discount_amount || 0));
	}

	if (owner) {
		owner.payment_value = fromFloyd(owner.payment_value || 0);
		owner.rent_value = fromFloyd(owner.rent_value || 0);
		owner.discounted_owner_rent_value = fromFloyd(owner.discounted_owner_rent_value || 0);
		owner.effective_rent_value = fromFloyd(owner.effective_rent_value || 0);
		if (owner && owner.extras && owner.extras.items) {
			for (const item of owner.extras.items) {
				item.amount = fromFloyd(item.amount || 0);
			}
			owner.extras.total = fromFloyd(owner.extras.total || 0);
		}
		if (owner.tourist_tax) {
			owner.tourist_tax = fromFloyd(owner.tourist_tax || 0);
		}
		if (owner.converted_tourist_tax) {
			owner.converted_tourist_tax = fromFloyd(
				owner.converted_tourist_tax || 0
			);
		}
	}

	if (travelNestBreakdown) {
		travelNestBreakdown.booking_balance = fromFloyd(travelNestBreakdown.booking_balance || 0);
		travelNestBreakdown.booking_discrepancy = fromFloyd(travelNestBreakdown.booking_discrepancy || 0);
		travelNestBreakdown.booking_discrepancy_net_value = fromFloyd(travelNestBreakdown.booking_discrepancy_net_value || 0);
		travelNestBreakdown.booking_discrepancy_vat_value = fromFloyd(travelNestBreakdown.booking_discrepancy_vat_value || 0);
		travelNestBreakdown.commission_value = fromFloyd(travelNestBreakdown.commission_value || 0);
		travelNestBreakdown.commission_net_value = fromFloyd(travelNestBreakdown.commission_net_value || 0);
		travelNestBreakdown.commission_vat_value = fromFloyd(travelNestBreakdown.commission_vat_value || 0);
		travelNestBreakdown.expected_booking_fee_net_value = fromFloyd(travelNestBreakdown.expected_booking_fee_net_value || 0);
		travelNestBreakdown.expected_booking_fee_vat_value = fromFloyd(travelNestBreakdown.expected_booking_fee_vat_value || 0);
		travelNestBreakdown.expected_booking_fee_value = fromFloyd(travelNestBreakdown.expected_booking_fee_value || 0);
		travelNestBreakdown.income_value = fromFloyd(travelNestBreakdown.income_value || 0);
		travelNestBreakdown.margin_value = fromFloyd(travelNestBreakdown.margin_value || 0);
		// 2021 commission columns
		if (travelNestBreakdown.presented_channel_value !== null && travelNestBreakdown.presented_channel_value !== undefined) {
			// perfectly viable for these columns to be null, but if one is null, they all will be.
			// if bookings were created before the commission change, these values will be null - not zero.
			travelNestBreakdown.presented_channel_value = fromFloyd(travelNestBreakdown.presented_channel_value || 0);
			travelNestBreakdown.presented_channel_net_value = fromFloyd(travelNestBreakdown.presented_channel_net_value || 0);
			travelNestBreakdown.presented_channel_vat_value = fromFloyd(travelNestBreakdown.presented_channel_vat_value || 0);
			travelNestBreakdown.presented_travelnest_value = fromFloyd(travelNestBreakdown.commission_value || 0);
			travelNestBreakdown.presented_travelnest_net_value = fromFloyd(travelNestBreakdown.presented_travelnest_net_value || 0);
			travelNestBreakdown.presented_travelnest_vat_value = fromFloyd(travelNestBreakdown.presented_travelnest_vat_value || 0);
			travelNestBreakdown.total_adjustment_commission_value = fromFloyd(travelNestBreakdown.total_adjustment_commission_value || 0);
			travelNestBreakdown.total_adjustment_commission_net_value = fromFloyd(travelNestBreakdown.total_adjustment_commission_net_value || 0);
			travelNestBreakdown.total_adjustment_commission_vat_value = fromFloyd(travelNestBreakdown.total_adjustment_commission_vat_value || 0);
		}
	}
	return bookingFromFloyd;
};

const transformOutboundBooking = (booking: IBookingV2): IBookingV2Api => {

	const floydionBooking = bookingToFloydionPrices(booking);

	let guestReceipts = { receipts: [] as IBookingReceiptApi[], status: 'unknown' };
	if (booking.guest_receipts) {
		guestReceipts = {
			receipts: transformOutboundReceipts(booking.guest_receipts.receipts || []),
			status: booking.guest_receipts.status
		};
	}

	let securityDeposit = { receipts: [] as IBookingReceiptApi[], status: 'unknown' };
	if (booking.security_deposit && booking.security_deposit.receipts.length > 0) {
		securityDeposit = {
			receipts: transformOutboundReceipts(booking.security_deposit.receipts || []),
			status: booking.security_deposit.status
		};
	}

	let ownerPayments = { payments: [] as IBookingReceiptApi[], status: 'unknown' };
	if (booking.owner_payments) {
		ownerPayments = {
			payments: transformOutboundReceipts(booking.owner_payments.payments || []),
			status: booking.owner_payments.status
		};
	}

	const apiBooking = {
		...floydionBooking,
		guest_receipts: guestReceipts,
		owner_payments: ownerPayments
	} as IBookingV2Api;

	if (booking.security_deposit && booking.security_deposit.receipts.length > 0) {
		apiBooking.security_deposit = securityDeposit;
	}
	return apiBooking;
};

const transformInboundBooking = (booking: IBookingV2Api): IBookingV2 => {

	const floydionBooking = bookingFromFloydionPrices(booking);

	let guestReceipts = { receipts: [] as IBookingReceipt[], status: 'unknown' };
	if (booking.guest_receipts) {
		guestReceipts = {
			receipts: transformInboundReceipts(booking.guest_receipts.receipts),
			status: booking.guest_receipts.status
		};
	}

	let securityDeposit = { receipts: [] as IBookingReceipt[], status: 'unknown' };
	if (booking.security_deposit) {
		securityDeposit = {
			receipts: transformInboundReceipts(booking.security_deposit.receipts),
			status: booking.security_deposit.status
		};
	}

	let ownerPayments = { payments: [] as IBookingReceipt[], status: 'unknown' };
	if (booking.owner_payments) {
		ownerPayments = {
			payments: transformInboundReceipts(booking.owner_payments.payments),
			status: booking.owner_payments.status
		};
	}

	const apiBooking = {
		...floydionBooking,
		guest_receipts: guestReceipts,
		owner_payments: ownerPayments
	} as IBookingV2;

	if (booking.security_deposit && booking.security_deposit.receipts.length > 0) {
		apiBooking.security_deposit = securityDeposit;
	}
	return apiBooking;
};

const transformOutboundReceipts = (receipts: IBookingReceipt[]): IBookingReceiptApi[] => {
	return receipts.map((receipt) => ({
		amount: parseFloat(toFloyd(receipt.amount)),
		currency: receipt.currency,
		date: stripTimezone(typeof receipt.date === 'string' ? receipt.date : receipt.date.toISOString()),
		method: receipt.method,
		reference: receipt.reference,
		created_at: receipt.created_at,
		updated_at: receipt.updated_at,
		on_date: stripTimezone(typeof receipt.date === 'string' ? receipt.date : receipt.date.toISOString()),
		id: receipt.id
	} as IBookingReceiptApi));
};

const transformInboundReceipts = (receipts: IBookingReceiptApi[]): IBookingReceipt[] => {
	return receipts.map((receipt) => ({
		amount: fromFloyd(receipt.amount),
		currency: receipt.currency,
		date: new Date(receipt.date),
		method: receipt.method,
		reference: receipt.reference,
		created_at: receipt.created_at,
		updated_at: receipt.updated_at,
		on_date: receipt.on_date,
		id: receipt.id
	} as IBookingReceipt));
};

export default BookingsApiFactory;
